/**
 * Grimaces du miroir déformant — PUR. Une caricature différente toutes les 4 s, dans un
 * ordre tiré au sort (jamais deux fois la même d'affilée), avec un fondu de l'une à l'autre.
 */
import { random, shuffled } from '../lib/random';

/** Poids de chaque effet de `deformFace` (0 : absent ; négatif : effet inverse). */
export interface Weights {
  /** Yeux grossis (négatif : rétrécis). */
  readonly eyes: number;
  readonly nose: number;
  /** Bouche étirée en largeur. */
  readonly mouthWide: number;
  /** Bouche grossie (en « O »). */
  readonly mouthOpen: number;
  /** Visage écrasé vers le nez (négatif : étiré). */
  readonly squash: number;
  /** Yeux rapprochés (qui louchent). */
  readonly cross: number;
  /** Menton qui tremble (amplitude, en distance entre les yeux). */
  readonly wobble: number;
}

const NONE: Weights = {
  eyes: 0,
  nose: 0,
  mouthWide: 0,
  mouthOpen: 0,
  squash: 0,
  cross: 0,
  wobble: 0,
};

export const GRIMACE_NAMES = [
  'bigEyes',
  'tinyEyes',
  'pinocchio',
  'frog',
  'bigMouth',
  'squash',
  'stretch',
  'crossEyed',
  'jelly',
] as const;
export type GrimaceName = (typeof GRIMACE_NAMES)[number];

export const GRIMACES: Record<GrimaceName, { label: string; weights: Weights }> = {
  bigEyes: { label: 'Yeux de hibou', weights: { ...NONE, eyes: 1 } },
  tinyEyes: { label: 'Yeux de taupe', weights: { ...NONE, eyes: -0.5, nose: 0.4 } },
  pinocchio: { label: 'Nez de Pinocchio', weights: { ...NONE, nose: 1.4 } },
  frog: { label: 'Bouche de grenouille', weights: { ...NONE, mouthWide: 1.5, eyes: 0.35 } },
  bigMouth: { label: 'Bouche en O', weights: { ...NONE, mouthOpen: 1.1, eyes: 0.3 } },
  squash: { label: 'Tête écrabouillée', weights: { ...NONE, squash: 0.45 } },
  stretch: { label: 'Tête d’allumette', weights: { ...NONE, squash: -0.45 } },
  crossEyed: { label: 'Yeux qui louchent', weights: { ...NONE, cross: 0.55, eyes: 0.3 } },
  jelly: { label: 'Menton en gelée', weights: { ...NONE, wobble: 0.3, mouthWide: 0.3 } },
};

/** Durée d'une grimace, dont le fondu vers la suivante à la fin. */
export const GRIMACE_MS = 4000;
export const BLEND_MS = 600;

/** Ordre des grimaces du cycle `cycle`. */
const order = (cycle: number, seed: number): GrimaceName[] =>
  shuffled(GRIMACE_NAMES, random(seed + cycle * 0x9e3779b1));

/**
 * `n`-ième grimace de la partie : chaque cycle passe par toutes, et un cycle ne commence
 * jamais par la dernière du précédent (seules ses deux premières places peuvent changer,
 * donc la dernière d'un cycle se calcule sans remonter plus loin).
 */
export function grimaceName(n: number, seed: number): GrimaceName {
  const count = GRIMACE_NAMES.length;
  const cycle = Math.floor(n / count);
  const names = order(cycle, seed);
  if (cycle > 0 && names[0] === order(cycle - 1, seed)[count - 1])
    [names[0], names[1]] = [names[1] as GrimaceName, names[0] as GrimaceName];
  return names[n % count] as GrimaceName;
}

export interface GrimaceFrame {
  readonly name: GrimaceName;
  readonly label: string;
  readonly weights: Weights;
}

const mix = (a: Weights, b: Weights, k: number): Weights => {
  const out = { ...a } as Record<keyof Weights, number>;
  for (const key of Object.keys(a) as (keyof Weights)[]) out[key] = a[key] + (b[key] - a[key]) * k;
  return out;
};

/** Grimace à l'instant `elapsedMs` de la partie (fondue vers la suivante en fin de créneau). */
export function grimaceAt(
  elapsedMs: number,
  seed: number,
  nameAt: (n: number) => GrimaceName = (n) => grimaceName(n, seed),
): GrimaceFrame {
  const t = Math.max(0, elapsedMs);
  const n = Math.floor(t / GRIMACE_MS);
  const name = nameAt(n);
  const { label, weights } = GRIMACES[name];
  const blend = (t - n * GRIMACE_MS - (GRIMACE_MS - BLEND_MS)) / BLEND_MS;
  if (blend <= 0) return { name, label, weights };
  return { name, label, weights: mix(weights, GRIMACES[nameAt(n + 1)].weights, blend) };
}
